import "./AppAside.scss";
import {NavLink, useLocation, useNavigate} from "react-router-dom";
import OnlineMenusIcon from "./icons/OnlineMenusIcon";
import MyBrandIcon from "./icons/MyBrandIcon";
import LinkPagesIcon from "./icons/LinkPagesIcon";
import {env} from "../env/env";
import LocationDropdown from "./LocationDropdown";
import {LocationDto, MemberDtoPlanEnum} from "../generated/dashboard-client";
import React, {useCallback, useEffect, useState} from "react";
import {useRecoilValue} from "recoil";
import {
  cloverDeltaUpdatesState,
  memberState,
  posState,
  squareDeltaUpdatesState,
  toastDeltaUpdatesState,
  treezDeltaUpdatesState
} from "../state/atoms";
import {ProStar} from "./account/misc/ProStar";
import SavedDesignsIcon from "./icons/SavedDesignsIcon";
import {
  BarsOutlined,
  CreditCardOutlined,
  DollarCircleOutlined, ExclamationCircleFilled,
  PrinterOutlined,
  SettingOutlined,
  StarFilled,
  UserOutlined,
} from "@ant-design/icons";
import ChangeLocationIcon from "./icons/ChangeLocationIcon";
import {Button} from "antd";
import {OmInUseModal} from "./om/OmInUseModal";
import {homePaths} from "../util/constants";
import {isAccountPath, isContentPath, isDSAPath, isLandingPath, isPrintPath, synchronousRequest} from "../util/utils";
import {hasUnresolvedToastUpdates, hasUnresolvedCloverUpdates, hasUnresolvedSquareUpdates, hasUnresolvedTreezUpdates} from "../service/PosService";
import TreezLogo from "./icons/TreezLogo";

interface AppAsideProps {
  activeLocation: LocationDto;
}

enum AsideMode {
  Dashboard,
  Account,
  Hidden,
}

export default function AppAside({
                                   activeLocation
                                 }: AppAsideProps) {
  const location = useLocation();
  const member = useRecoilValue(memberState);
  const pos = useRecoilValue(posState);
  const [mode, setMode] = useState(AsideMode.Dashboard);
  const [isOoLoading, setOoLoading] = useState(false);
  const [isOoInUse, setOoInUse] = useState(false);
  const toastDeltaUpdates = useRecoilValue(toastDeltaUpdatesState);
  const cloverDeltaUpdates = useRecoilValue(cloverDeltaUpdatesState);
  const squareDeltaUpdates = useRecoilValue(squareDeltaUpdatesState);
  const treezDeltaUpdates = useRecoilValue(treezDeltaUpdatesState);
  const [hasUnsolvedToastChanges, setHasUnsolvedToastChanges] = useState(false);
  const [hasUnsolvedCloverChanges, setHasUnsolvedCloverChanges] = useState(false);
  const [hasUnsolvedSquareChanges, setHasUnsolvedSquareChanges] = useState(false);
  const [hasUnsolvedTreezChanges, setHasUnsolvedTreezChanges] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    setHasUnsolvedToastChanges(hasUnresolvedToastUpdates(toastDeltaUpdates));
  }, [toastDeltaUpdates]);

  useEffect(() => {
    setHasUnsolvedCloverChanges(hasUnresolvedCloverUpdates(cloverDeltaUpdates));
  }, [cloverDeltaUpdates]);

  useEffect(() => {
    setHasUnsolvedSquareChanges(hasUnresolvedSquareUpdates(squareDeltaUpdates));
  }, [squareDeltaUpdates]);

  useEffect(() => {
    setHasUnsolvedTreezChanges(hasUnresolvedTreezUpdates(treezDeltaUpdates));
  }, [treezDeltaUpdates]);

  function goToTeam() {
    window.location.href = `${env.dashboardPath}/account/team`;
  }

  function goPro() {
    window.MHM.ProModal.upgrade({memberId: member.id, onSuccess: () => window.location.reload()});
  }

  function goToDesignServices() {
    window.open("/feature/menu-services.html", "_blank");
  }

  const onOnlineMenuClick = useCallback(() => {
    if (member.plan === MemberDtoPlanEnum.Free) {
      navigate(`${env.dashboardPath}/online-menus/`);
      return;
    }

    setOoLoading(true);
    setTimeout(() => {
      const resp = synchronousRequest(`/app/online/location/stats/${activeLocation.internalId!}?oo=true`);
      let location: any | null = null;
      if (resp) {
        try {
          location = JSON.parse(JSON.parse(resp).data).location;
        } catch (e) {
          console.error(e);
        }
      }

      if (location?.inUse) {
        setOoInUse(true);
        setOoLoading(false);
      } else {
        const base = 'https://ohbz.com';//window.location.href.indexOf("staging.") > -1 ? "https://www.musthavemenus.com" : env.basePath;
        const tkn = window.localStorage.getItem("tkn");
        window.open(`${base}${env.ooContextPath}?businessId=${activeLocation.internalId}&tkn=${tkn}`, '_blank');
        setOoLoading(false);
      }
    }, 200);
  }, [activeLocation.internalId, navigate, member.plan])

  useEffect(() => {
    const {pathname} = location;

    if (isPrintPath(pathname) || isLandingPath(pathname) || isContentPath(pathname) || isDSAPath(pathname)) {
      setMode(AsideMode.Hidden);
    } else if (isAccountPath(pathname)) {
      setMode(AsideMode.Account);
    } else {
      setMode(AsideMode.Dashboard);
    }
  }, [location]);

  return (
    <>
      {mode !== AsideMode.Hidden && (
        <aside className={"main"}>
          {mode === AsideMode.Dashboard && (
            <>
              <LocationDropdown location={activeLocation}/>
              <nav>
                <ul>
                  <li>
                    <NavLink
                      to={`${env.dashboardPath}/designs/${activeLocation.internalId}`}
                      className={
                        homePaths.includes(window.location.pathname)
                          ? "active"
                          : ""
                      }
                    >
                      <SavedDesignsIcon/>
                      Saved Designs
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to={`${env.dashboardPath}/dsa/manage`}>
                      <img className="dsa-icon" src="https://timber.mhmcdn.com/site/marketing/icons/DSATeal.svg" alt="display teal"/>
                      <span style={{margin: 0}}>Display</span>
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to={`${env.dashboardPath}/design-updater`}>
                      {pos.enabled && pos.isToast && <img className="pos-icon" src="https://timber.mhmcdn.com/site/marketing/icons/Toast.svg" alt="Toast small icon"/>}
                      {pos.enabled && pos.isSquare && <img className="pos-icon" src="https://timber.mhmcdn.com/site/marketing/icons/Square.svg" alt="Square small icon"/>}
                      {pos.enabled && pos.isTreez && <TreezLogo/>}
                      {pos.enabled && !pos.isSquare && !pos.isToast && !pos.isTreez && !pos.isClover && <BarsOutlined />}
                      POS Integration {(hasUnsolvedToastChanges || hasUnsolvedCloverChanges || hasUnsolvedSquareChanges || hasUnsolvedTreezChanges) && pos.enabled &&
                      <span className={'exclamation-icon'}><ExclamationCircleFilled /></span>}
                    </NavLink>
                  </li>
                  <li>
                    <Button
                      onClick={onOnlineMenuClick}
                      type={"link"}
                      loading={isOoLoading}
                      className={location.pathname.indexOf("online-menus") > -1 ? "active" : ""}
                    >
                      {!isOoLoading && <OnlineMenusIcon/>}
                      Online Menus
                    </Button>
                  </li>
                  <li>
                    <NavLink
                      className={location.pathname.indexOf("link-pages") > -1 ? "active" : ""}
                      to={`${env.dashboardPath}/link-pages/${activeLocation.internalId}`}
                    >
                      <LinkPagesIcon/>
                      Link Pages
                    </NavLink>
                  </li>
                  {/*<li>
                    <NavLink to="/content/bento" >
                      <WebsiteIcon />
                      Websites
                      <img style={{ height: "15px", marginLeft: "10px"}} src={bentoLogo} alt={"Logo"} />
                    </NavLink>
                  </li>*/}
                  <li>
                    <NavLink
                      className={location.pathname.indexOf("brand") > -1 ? "active" : ""}
                      to={`${env.dashboardPath}/brand/${activeLocation.internalId}`}
                    >
                      <MyBrandIcon/>
                      Brand
                    </NavLink>
                  </li>
                </ul>
              </nav>

              <footer>
                <div className="btn-container">
                  <Button onClick={goToDesignServices} className={"primary ds-btn"}>
                    <img src="https://timber.mhmcdn.com/site/marketing/icons/PaintbrushWhite.svg" alt="white paintbrush" />
                    Menu Services
                  </Button>
                </div>
                {member.owner && !member.clover && (
                  <div className={"btn-container"}>
                    {member.plan === MemberDtoPlanEnum.Free ? (
                      <Button onClick={goPro} className={"go-pro"}>
                        <StarFilled/>
                        Go Pro
                      </Button>
                    ) : (
                      !member.clover && <Button onClick={goToTeam} className={"secondary-two add-team"}>
                        <UserOutlined/>
                        Add Your Team
                      </Button>
                    )}
                  </div>
                )}
              </footer>
            </>
          )}

          {mode === AsideMode.Account && (
            <>
              <nav className={"account-aside"}>
                <ul>
                  <li>
                    <NavLink
                      className={"user-info"}
                      to={env.dashboardPath + "/account/users"}
                    >
                      <SettingOutlined/>
                      User Info
                    </NavLink>
                  </li>
                  {member.plan !== MemberDtoPlanEnum.Free && member.owner && !member.clover && (
                    <li>
                      <NavLink
                        className={"billing-info"}
                        to={env.dashboardPath + "/account/billing"}
                      >
                        <CreditCardOutlined/>
                        Billing Info
                      </NavLink>
                    </li>
                  )}
                  {member.owner && (
                    <li>
                      <NavLink
                        className={"subscription"}
                        to={env.dashboardPath + "/account/plan"}
                      >
                        <DollarCircleOutlined/>
                        Subscription
                      </NavLink>
                    </li>
                  )}
                  {member.owner && !member.clover && (
                    <li>
                      <NavLink
                        className={"locations"}
                        to={env.dashboardPath + "/account/locations"}
                      >
                        <ChangeLocationIcon/>
                        Locations
                        {member.plan !== MemberDtoPlanEnum.Premium && (
                          <ProStar/>
                        )}
                      </NavLink>
                    </li>
                  )}
                  {member.owner && !member.clover && (
                    <li>
                      <NavLink
                        className={"team"}
                        to={env.dashboardPath + "/account/team"}
                      >
                        <UserOutlined/>
                        Team
                        {member.plan !== MemberDtoPlanEnum.Premium && (
                          <ProStar/>
                        )}{" "}
                      </NavLink>
                    </li>
                  )}
                  {member.owner && (
                    <li>
                      <NavLink
                        className={"print-history"}
                        to={env.dashboardPath + "/account/print"}
                      >
                        <PrinterOutlined/>
                        Print History
                      </NavLink>
                    </li>
                  )}
                </ul>
              </nav>
            </>
          )}
        </aside>
      )}

      <OmInUseModal
        isVisible={isOoInUse}
        onClose={() => {
          setOoInUse(false);
          setOoLoading(false);
        }}
      />
    </>
  );
}
